// Cart storage utility for persisting cart items in localStorage

export interface CartItem {
  id: string;
  type: 'product' | 'accessory';
  name: string;
  imageUrl?: string;
  // For products
  planId?: string;
  planName?: string;
  productId?: string;
  productShortName?: string;
  fallDetection?: boolean;
  /** LifeBeacon only: wear as pendant or watch. EZ Care / EZ Care Plus ignore. */
  deviceStyle?: 'pendant' | 'watch';
  // For accessories
  accessoryId?: string;
  price?: number;
  billingCycle?: 'monthly' | 'annual' | 'one-time';
}

const CART_STORAGE_KEY = 'alert1_cart_items';
const CHECKOUT_FORM_STORAGE_KEY = 'alert1_checkout_form';

// Cart Items Management
export const cartStorage = {
  // Get all cart items
  getItems(): CartItem[] {
    try {
      const items = localStorage.getItem(CART_STORAGE_KEY);
      return items ? JSON.parse(items) : [];
    } catch (error) {
      console.error('Error reading cart from storage:', error);
      return [];
    }
  },

  // Add item to cart
  addItem(item: CartItem): void {
    const items = this.getItems();
    // Check if item already exists by ID first (most reliable)
    const existingByIdIndex = items.findIndex(i => i.id === item.id);
    
    if (existingByIdIndex >= 0) {
      // Update existing item with same ID
      items[existingByIdIndex] = item;
    } else {
      // Check for duplicates based on type-specific logic
      const existingIndex = items.findIndex(i => {
        if (item.type === 'product' && i.type === 'product') {
          return i.planId === item.planId && i.productId === item.productId && 
                 i.fallDetection === item.fallDetection;
        }
        if (item.type === 'accessory' && i.type === 'accessory') {
          // For protection plans (accessory-1), use ID to allow multiple per product
          // For other accessories, use accessoryId + billingCycle
          const isProtectionPlan = item.name?.toLowerCase().includes('protection plan') || 
                                   item.accessoryId === 'accessory-1';
          if (isProtectionPlan) {
            // Protection plans should be unique by ID (which includes product ID)
            // So we already checked by ID above, this won't match
            return false;
          }
          // For non-protection accessories, check by accessoryId + billingCycle
          return i.accessoryId === item.accessoryId && i.billingCycle === item.billingCycle;
        }
        return false;
      });

      if (existingIndex >= 0) {
        // Update existing item
        items[existingIndex] = item;
      } else {
        // Add new item
        items.push(item);
      }
    }

    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to storage:', error);
    }
  },

  // Remove item from cart by index
  removeItem(index: number): void {
    const items = this.getItems();
    items.splice(index, 1);
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error removing item from cart:', error);
    }
  },

  // Remove item from cart by ID
  removeItemById(itemId: string): void {
    const items = this.getItems();
    const filteredItems = items.filter(item => item.id !== itemId);
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(filteredItems));
    } catch (error) {
      console.error('Error removing item from cart:', error);
    }
  },

  // Clear all items
  clear(): void {
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  },

  // Get item count
  getItemCount(): number {
    return this.getItems().length;
  }
};

// Checkout Form Data Management
export const checkoutFormStorage = {
  // Save form data
  save(data: any): void {
    try {
      localStorage.setItem(CHECKOUT_FORM_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving checkout form data:', error);
    }
  },

  // Load form data
  load(): any {
    try {
      const data = localStorage.getItem(CHECKOUT_FORM_STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error loading checkout form data:', error);
      return null;
    }
  },

  // Clear form data
  clear(): void {
    try {
      localStorage.removeItem(CHECKOUT_FORM_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing checkout form data:', error);
    }
  }
};

